// Resource type to route mapping
export const resourcesTypes = {
  GLOBAL_TRANSLATION: 'templates/translations/edit',
  MODULE: 'modules/local/edit',
  PROCESS: 'processes/edit',
  PROCESS_CREDENTIAL: 'credentials/edit',
  SETTING: 'settings/edit',
  TEXT_TEMPLATE: 'templates/templates/edit',
  TRIGGER_CRON: 'triggers/cron/edit',
  TRIGGER_REST: 'triggers/rest/edit',
  VALIDATION_RULE: 'validations/edit',
  PLUGIN: 'plugins/edit',
  TRIGGER_MESSAGING: 'triggers/messaging/edit',
  LIBRARY: 'libraries/edit',
  TRIGGER_MCP: 'triggers/mcp/edit',
  TRIGGER_EVENT_HANDLER: 'triggers/event-handler/edit',
  TRIGGER_UI: 'triggers/ui/edit',
  TRIGGER_WHATSAPP: 'triggers/whatsapp/edit',
  APPLICATION: 'applications/edit',
  ENTITY: 'entities/edit',
  AI_AGENT: 'aiAgents/edit',
  SCREEN: 'screen/edit',
  FLOW: 'flow/edit',
  STATIC_RESOURCE: 'static-resources/edit'
}

// Master list of all resource types (order doesn't matter - will be sorted by translation)
export const RESOURCE_TYPES = [
  'AI_AGENT',
  'APPLICATION',
  'ENTITY',
  'FLOW',
  'GLOBAL_TRANSLATION',
  'LIBRARY',
  'MODULE',
  'PLUGIN',
  'PROCESS',
  'PROCESS_CREDENTIAL',
  'SCREEN',
  'SETTING',
  'STATIC_RESOURCE',
  'TEXT_TEMPLATE',
  'TRIGGER_CRON',
  'TRIGGER_EVENT_HANDLER',
  'TRIGGER_MCP',
  'TRIGGER_MESSAGING',
  'TRIGGER_REST',
  'TRIGGER_UI',
  'TRIGGER_WHATSAPP',
  'VALIDATION_RULE'
]

// Resource types a module may list for deletion. The backend rejects
// APPLICATION there, an application stops containers and cannot take part in
// the transactional deletion.
export const MODULE_DELETABLE_RESOURCE_TYPES = RESOURCE_TYPES.filter((type) => type !== 'APPLICATION')

// Subset of resource types available for module import
export const IMPORT_RESOURCE_TYPES = [
  'AI_AGENT',
  'GLOBAL_TRANSLATION',
  'MODULE',
  'PLUGIN',
  'PROCESS',
  'PROCESS_CREDENTIAL',
  'SCREEN',
  'SETTING',
  'TEXT_TEMPLATE',
  'TRIGGER_CRON',
  'TRIGGER_MCP',
  'TRIGGER_REST',
  'TRIGGER_WHATSAPP'
]

export const GDPR_RESOURCE_TYPES = [
  'gdprAiAgents',
  'gdprApplications',
  'gdprCronTrigger',
  'gdprEntities',
  'gdprEventHandlerTrigger',
  'gdprFlows',
  'gdprLibraries',
  'gdprMcpTrigger',
  'gdprMessagingTrigger',
  'gdprModules',
  'gdprProcessCredentials',
  'gdprProcesses',
  'gdprRestTrigger',
  'gdprScreens',
  'gdprTriggerUis',
  'gdprWhatsappTrigger'
]

/**
 * Sorts resource types alphabetically based on their translated labels
 * @param {Array} types - Array of resource type keys
 * @param {Object} translations - The $lang.status object containing translations
 * @returns {Array} - Sorted array of resource type keys
 */
export function sortResourceTypesByLabel(types, translations) {
  return [...types].sort((a, b) => {
    const labelA = (translations[a] || a).toLowerCase()
    const labelB = (translations[b] || b).toLowerCase()
    return labelA.localeCompare(labelB)
  })
}

export function getStepIconName(stepType = '') {
  switch (stepType.toUpperCase()) {
  case 'PASTE':
    return 'step-dialog'
  case 'COUNTER':
    return 'counter'
  case 'WAIT':
    return 'timelapse'
  case 'FLOWY_CACHE':
    return 'memory'
  case 'OUTLOOK_CALENDAR':
    return 'calendar-check-outline'
  case 'XLS':
    return 'file-excel-outline'
  case 'XML':
    return 'file-code-outline'
  case 'XSLT':
    return 'code-tags'
  case 'YAML':
    return 'file-code'
  case 'JSON':
    return 'code-json'
  case 'LOG':
    return 'text-box-outline'
  case 'ESC_CHARS':
    return 'printer-pos'
  case 'OCR':
    return 'scanner'
  case 'QR':
    return 'qrcode'
  case 'BARCODE':
    return 'barcode'
  case 'MCP':
    return 'server-outline'
  case 'PROCESS_SETTING':
    return 'tune-variant'
  case 'TIMER':
    return 'timer-outline'
  case 'ZIP':
    return 'folder-zip-outline'
  case 'EXECUTE_EXTERNAL_COMMAND':
    return 'console'
  case 'EXECUTE_PROCESS':
    return 'play-box-outline'
  case 'FORMATTING':
    return 'format-align-left'
  case 'SET_VARIABLES':
    return 'form-textbox'
  case 'PYTHON':
    return 'language-python'
  case 'QUERY_BUILDER':
    return 'database-search'
  case 'UUID':
    return 'fingerprint'
  case 'CLOUD_FRONT':
    return 'cloud-outline'
  case 'FTP':
    return 'folder-outline'
  case 'GRAPHQL':
    return 'graphql'
  case 'RSS':
    return 'broadcast'
  case 'JWT':
    return 'key-outline'
  case 'LDAP':
    return 'account-multiple-outline'
  case 'SSH':
    return 'lock-smart'
  case 'PAYMENT_SENSE_CONNECT_E':
    return 'credit-card-wireless-outline'
  case 'PAYMENT_SENSE_PAC':
    return 'credit-card-lock-outline'
  case 'I_CALENDAR':
    return 'calendar-month-outline'
  case 'CHAT_GPT':
    return 'robot-outline'
  case 'VALIDATION':
    return 'magnify-scan'
  case 'CLAUDE':
    return 'flare'
  case 'DEEP_SEEK':
    return 'dolphin'
  case 'ENCODER':
    return 'cog-transfer-outline'
  case 'TOON':
    return 'cog-transfer-outline'
  case 'PEPPOL':
    return 'file-send-outline'
  case 'TIKA':
    return 'file-search-outline'
  case 'TIME_HANDLER':
    return 'clock-time-four-outline'
  case 'CREDENTIAL':
    return 'lock'
  case 'EMAIL':
    return 'email-outline'
  case 'GROOVY':
    return 'code-tags'
  case 'JS':
    return 'language-javascript'
  case 'IMAGE':
    return 'image'
  case 'JDBC':
  case 'MONGODB':
    return 'database-outline'
  case 'NEO4J':
    return 'graph-outline'
  case 'GOTENBERG':
    return 'file-export-outline'
  case 'MCP_SSE':
  case 'MCP_HTTP':
  case 'MCP_STDIO':
  case 'MCP_OPENAI_CONNECTOR':
    return 'server-outline'
  case 'IMAP':
    return 'email-open-outline'
  case 'PDF':
    return 'file-pdf-box'
  case 'LATEX':
    return 'file-document-edit-outline'
  case 'PLUGIN':
    return 'power-plug-outline'
  case 'REST':
    return 'web'
  case 'SOAP':
    return 'xml'
  case 'S3':
    return 'warehouse'
  case 'SLACK':
    return 'slack'
  case 'SWITCH':
    return 'shuffle'
  case 'EXECUTE_PARALLEL_STEPS':
    return 'math-norm'
  case 'FOREACH':
    return 'swap-horizontal'
  case 'USER':
    return 'account'
  case 'TWILIO':
    return 'message-processing'
  case 'WHILE':
  case 'DO_WHILE':
    return 'rotate-left'
  case 'IF':
    return 'code-block-braces'
  case 'UNTIL':
    return 'code-block-parentheses'
  case 'DO_UNTIL':
    return 'code-block-parentheses'
  case 'CSV FILE':
    return 'file-delimited'
  case 'TRY':
    return 'flask-empty-outline'
  case 'FINALLY':
    return 'flask-plus'
  case 'EXCEPTION':
    return 'flask-minus'
  case 'QUERY':
    return 'help'
  case 'BATCH':
    return 'help'
  case 'UNSET_VARIABLES':
    return 'broom'
  case 'SECURITY':
    return 'lock'
  case 'MESSAGING':
    return 'message'
  case 'TRY_CATCH':
    return 'flask'
  case 'CATCH':
    return 'flask-minus'
  case 'STORAGE':
    return 'server'
  case 'SSH_KEY':
    return 'key-variant'
  case 'MS_GRAPH_API':
    return 'calendar-account-outline'
  case 'SMTP':
    return 'email'
  case 'SFTP':
    return 'folder'
  case 'RABBITMQ':
    return 'rabbit'
  case 'OPEN_AI':
    return 'robot-outline'
  case 'MQTT':
    return 'cloud-outline'
  case 'KAFKA':
    return 'apache-kafka'
  case 'JMS':
    return 'message'
  case 'FLOWY':
    return 'sitemap'
  case 'ANTHROPIC':
    return 'flare'
  case 'JIRA':
    return 'jira'
  case 'STRIPE_CONNECT':
    return 'credit-card'
  case 'AWS':
    return 'aws'
  case 'SCRIPT':
    return 'script'
  case 'REST_BASIC':
    return 'shield-key'
  case 'REST_BEARER':
    return 'shield-key-outline'
  case 'EXECUTE_AI_AGENT':
    return 'robot-happy'
  case 'THROW_EXCEPTION':
    return 'code-brackets'
  case 'WHATSAPP':
    return 'whatsapp'
  default:
    return 'circle'
  }
}

export const stepExceptions = {
  FTP: ['IOException', 'FtpException', 'CacheFileCastException'],
  CSV: ['IOException', 'InvalidCsvSourceStepException'],
  EXECUTE_EXTERNAL_COMMAND: ['IOException', 'TimeoutException'],
  EXECUTE_PROCESS: ['ChildProcessNotFoundException', 'ChildEventOfParentProcessNotFoundException', 'ChildProcessTimeoutException', 'ProcessDisabledException'],
  FOREACH: [],
  GROOVY: ['MultipleCompilationErrorsException', 'CompilationFailedException', 'MissingPropertyException', 'MissingClassException', 'MissingFieldException', 'MissingMethodException', 'GroovyRuntimeException', 'StringWriterIOException'],
  IMAGE: ['IOException', 'MalformedURLException', 'XRRuntimeException'],
  IMAP: ['NoSuchProviderException', 'MessagingException', 'ImapMessageNotFoundException'],
  JDBC: ['SQLException', 'NotSingleResultException'],
  JS: ['PolyglotException', 'LibraryDisabledException', 'MissedLibraryArtifactException', 'InvalidLibraryTypeException'],
  JSON: ['JException'],
  JWT: ['NoSuchAlgorithmException', 'InvalidKeySpecException', 'InvalidKeyException'],
  MESSAGING: [],
  MONGODB: ['MongoChangeStreamException', 'MongoClientException', 'MongoConfigurationException', 'MongoSecurityException', 'MongoTimeoutException', 'MongoWaitQueueFullException', 'MongoExecutionTimeoutException',
    'MongoGridFSException', 'MongoIncompatibleDriverException', 'MongoInternalException', 'MongoInterruptedException', 'MongoServerException', 'BulkWriteException', 'MongoBulkWriteException', 'MongoCommandException',
    'MongoNodeIsRecoveringException', 'MongoNotPrimaryException', 'MongoQueryException', 'MongoCursorNotFoundException', 'MongoWriteConcernException', 'MongoWriteException', 'WriteConcernException', 'DuplicateKeyException',
    'MongoSocketException', 'MongoSocketClosedException', 'MongoSocketOpenException', 'MongoSocketReadException', 'MongoSocketReadTimeoutException', 'MongoSocketWriteException', 'NotSingleResultException'],
  NEO4J: [],
  GOTENBERG: [],
  PDF: ['IOException', 'DocumentException', 'IndexOutOfBoundsException'],
  LATEX: ['TextTemplateException'],
  PLUGIN: ['PluginDisabledException', 'PluginNotFoundException', 'PluginSettingDisabledException', 'PluginSettingNotFoundException', 'BundleException', 'ClassNotFoundException', 'NoSuchMethodException', 'InvocationTargetException'],
  PROCESS_SETTING: ['ProcessSettingsDisabledException', 'ProcessSettingsNotFoundException'],
  REST: ['JsonProcessingException', 'IllegalArgumentException'],
  S3: ['S3BucketRequiredException', 'JsonProcessingException', 'IOException', 'NoSuchKeyException', 'InvalidObjectStateException', 'SdkException', 'SdkClientException', 'S3Exception', 'NoSuchBucketException'],
  SECURITY: ['JsonProcessingException', 'JsonMappingException', 'NoSuchAlgorithmException', 'InvalidKeySpecException', 'NoSuchPaddingException', 'InvalidKeyException', 'InvalidAlgorithmParameterException', 'IllegalBlockSizeException', 'BadPaddingException'],
  SLACK: ['IOException', 'SlackApiException'],
  SMTP: ['JsonProcessingException', 'MessagingException'],
  EMAIL: ['JsonProcessingException', 'MessagingException'],
  SWITCH: [],
  EXECUTE_PARALLEL_STEPS: ['BatchExecutionException'],
  TRY_CATCH: ['RestClientException'],
  TWILIO: [],
  UNSET_VARIABLES: [],
  USER: ['InvalidUserIdException', 'RoleNotAssignableException', 'UserEmailAlreadyExistsException'],
  WHILE: [],
  DO_WHILE: [],
  IF: [],
  WHATSAPP: [],
  PAYMENT_SENSE_CONNECT_E: ['RestClientException'],
  PAYMENT_SENSE_PAC: ['RestClientException'],
  FORMATTING: ['IllegalArgumentException'],
  PYTHON: ['JepException'],
  XSLT: ['XsltProcessStepsException'],
  GRAPHQL: ['JsonProcessingException', 'IllegalArgumentException'],
  ENCODER: ['IllegalArgumentException'],
  TOON: [],
  TIKA: [],
  QR: ['QRDataTooBigException'],
  BARCODE: [],
  COUNTER: ['MappingException'],
  FLOWY_CACHE: ['CacheFileParsingException', 'PathNotFoundException'],
  CLAUDE: ['AnthropicException'],
  DEEP_SEEK: ['HttpClientErrorException'],
  RSS: ['ResourceAccessException', 'HttpStatusCodeException','RssUrlRequiredException','InvalidRssUrlException','HttpClientErrorException','JsonProcessingException','IllegalArgumentException'],
  JIRA: [],
  STRIPE_CONNECT: ['StripeException'],
  MCP: ['McpException'],
  TIME_HANDLER: ['DateTimeParseException', 'MappingException'],
  VALIDATION: ['ValidationStepException', 'IllegalStateException', 'HttpNotFoundException'],
  THROW_EXCEPTION: [],
  EXECUTE_AI_AGENT: ['AiAgentProcessNotFoundException', 'InterruptProcessException', 'ChildEventOfParentProcessNotFoundException', 'ChildProcessTimeoutException', 'ProcessDisabledException' ]
}

export const localesFormatting = [
  { title: 'Albania (sq_AL)', value: 'sq_AL' },
  { title: 'Algeria (ar_DZ)', value: 'ar_DZ' },
  { title: 'Bahrain (ar_BH)', value: 'ar_BH' },
  { title: 'Egypt (ar_EG)', value: 'ar_EG' },
  { title: 'Iraq (ar_IQ)', value: 'ar_IQ' },
  { title: 'Jordan (ar_JO)', value: 'ar_JO' },
  { title: 'Kuwait (ar_KW)', value: 'ar_KW' },
  { title: 'Lebanon (ar_LB)', value: 'ar_LB' },
  { title: 'Libya (ar_LY)', value: 'ar_LY' },
  { title: 'Morocco (ar_MA)', value: 'ar_MA' },
  { title: 'Oman (ar_OM)', value: 'ar_OM' },
  { title: 'Qatar (ar_QA)', value: 'ar_QA' },
  { title: 'Saudi Arabia (ar_SA)', value: 'ar_SA' },
  { title: 'Sudan (ar_SD)', value: 'ar_SD' },
  { title: 'Syria (ar_SY)', value: 'ar_SY' },
  { title: 'Tunisia (ar_TN)', value: 'ar_TN' },
  { title: 'United Arab Emirates (ar_AE)', value: 'ar_AE' },
  { title: 'Yemen (ar_YE)', value: 'ar_YE' },
  { title: 'Belarus (be_BY)', value: 'be_BY' },
  { title: 'Bulgaria (bg_BG)', value: 'bg_BG' },
  { title: 'Spain (ca_ES)', value: 'ca_ES' },
  { title: 'China (zh_CN)', value: 'zh_CN' },
  { title: 'Singapore (zh_SG)', value: 'zh_SG' },
  { title: 'Hong Kong (zh_HK)', value: 'zh_HK' },
  { title: 'Taiwan (zh_TW)', value: 'zh_TW' },
  { title: 'Croatia (hr_HR)', value: 'hr_HR' },
  { title: 'Czech Republic (cs_CZ)', value: 'cs_CZ' },
  { title: 'Denmark (da_DK)', value: 'da_DK' },
  { title: 'Belgium (nl_BE)', value: 'nl_BE' },
  { title: 'Netherlands (nl_NL)', value: 'nl_NL' },
  { title: 'Australia (en_AU)', value: 'en_AU' },
  { title: 'Canada (en_CA)', value: 'en_CA' },
  { title: 'India (en_IN)', value: 'en_IN' },
  { title: 'Ireland (en_IE)', value: 'en_IE' },
  { title: 'Malta (en_MT)', value: 'en_MT' },
  { title: 'New Zealand (en_NZ)', value: 'en_NZ' },
  { title: 'Philippines (en_PH)', value: 'en_PH' },
  { title: 'Singapore (en_SG)', value: 'en_SG' },
  { title: 'South Africa (en_ZA)', value: 'en_ZA' },
  { title: 'United Kingdom (en_GB)', value: 'en_GB' },
  { title: 'United States (en_US)', value: 'en_US' },
  { title: 'Estonia (et_EE)', value: 'et_EE' },
  { title: 'Finland (fi_FI)', value: 'fi_FI' },
  { title: 'Belgium (fr_BE)', value: 'fr_BE' },
  { title: 'Canada (fr_CA)', value: 'fr_CA' },
  { title: 'France (fr_FR)', value: 'fr_FR' },
  { title: 'Luxembourg (fr_LU)', value: 'fr_LU' },
  { title: 'Switzerland (fr_CH)', value: 'fr_CH' },
  { title: 'Austria (de_AT)', value: 'de_AT' },
  { title: 'Germany (de_DE)', value: 'de_DE' },
  { title: 'Luxembourg (de_LU)', value: 'de_LU' },
  { title: 'Switzerland (de_CH)', value: 'de_CH' },
  { title: 'Cyprus (el_CY)', value: 'el_CY' },
  { title: 'Greece (el_GR)', value: 'el_GR' },
  { title: 'Israel (iw_IL)', value: 'iw_IL' },
  { title: 'India (hi_IN)', value: 'hi_IN' },
  { title: 'Hungary (hu_HU)', value: 'hu_HU' },
  { title: 'Iceland (is_IS)', value: 'is_IS' },
  { title: 'Indonesia (in_ID)', value: 'in_ID' },
  { title: 'Ireland (ga_IE)', value: 'ga_IE' },
  { title: 'Italy (it_IT)', value: 'it_IT' },
  { title: 'Switzerland (it_CH)', value: 'it_CH' },
  { title: 'Japan (ja_JP)', value: 'ja_JP' },
  { title: 'Japan (ja_JP_u_ca_japanese)', value: 'ja_JP_u_ca_japanese' },
  { title: 'Japan (ja_JP_x_lvariant_JP)', value: 'ja_JP_x_lvariant_JP' },
  { title: 'South Korea (ko_KR)', value: 'ko_KR' },
  { title: 'Latvia (lv_LV)', value: 'lv_LV' },
  { title: 'Lithuania (lt_LT)', value: 'lt_LT' },
  { title: 'Macedonia (mk_MK)', value: 'mk_MK' },
  { title: 'Malaysia (ms_MY)', value: 'ms_MY' },
  { title: 'Malta (mt_MT)', value: 'mt_MT' },
  { title: 'Norway (no_NO)', value: 'no_NO' },
  { title: 'Norway (nb_NO)', value: 'nb_NO' },
  { title: 'Norway (nn_NO)', value: 'nn_NO' },
  { title: 'Norway (no_NO_x_lvariant_NY)', value: 'no_NO_x_lvariant_NY' },
  { title: 'Poland (pl_PL)', value: 'pl_PL' },
  { title: 'Brazil (pt_BR)', value: 'pt_BR' },
  { title: 'Portugal (pt_PT)', value: 'pt_PT' },
  { title: 'Romania (ro_RO)', value: 'ro_RO' },
  { title: 'Russia (ru_RU)', value: 'ru_RU' },
  { title: 'Bosnia and Herzegovina (sr_BA)', value: 'sr_BA' },
  { title: 'Montenegro (sr_ME)', value: 'sr_ME' },
  { title: 'Serbia (sr_RS)', value: 'sr_RS' },
  { title: 'Bosnia and Herzegovina (sr_Latn_BA)', value: 'sr_Latn_BA' },
  { title: 'Montenegro (sr_Latn_ME)', value: 'sr_Latn_ME' },
  { title: 'Serbia (sr_Latn_RS)', value: 'sr_Latn_RS' },
  { title: 'Slovakia (sk_SK)', value: 'sk_SK' },
  { title: 'Argentina (es_AR)', value: 'es_AR' },
  { title: 'Bolivia (es_BO)', value: 'es_BO' },
  { title: 'Chile (es_CL)', value: 'es_CL' },
  { title: 'Colombia (es_CO)', value: 'es_CO' },
  { title: 'Costa Rica (es_CR)', value: 'es_CR' },
  { title: 'Dominican Republic (es_DO)', value: 'es_DO' },
  { title: 'Ecuador (es_EC)', value: 'es_EC' },
  { title: 'El Salvador (es_SV)', value: 'es_SV' },
  { title: 'Guatemala (es_GT)', value: 'es_GT' },
  { title: 'Honduras (es_HN)', value: 'es_HN' },
  { title: 'Mexico (es_MX)', value: 'es_MX' },
  { title: 'Nicaragua (es_NI)', value: 'es_NI' },
  { title: 'Panama (es_PA)', value: 'es_PA' },
  { title: 'Paraguay (es_PY)', value: 'es_PY' },
  { title: 'Peru (es_PE)', value: 'es_PE' },
  { title: 'Puerto Rico (es_PR)', value: 'es_PR' },
  { title: 'Spain (es_ES)', value: 'es_ES' },
  { title: 'United States (es_US)', value: 'es_US' },
  { title: 'Uruguay (es_UY)', value: 'es_UY' },
  { title: 'Venezuela (es_VE)', value: 'es_VE' },
  { title: 'Sweden (sv_SE)', value: 'sv_SE' },
  { title: 'Thailand (th_TH)', value: 'th_TH' },
  { title: 'Thailand (th_TH_u_ca_buddhist)', value: 'th_TH_u_ca_buddhist' },
  { title: 'Thailand (th_TH_u_ca_buddhist_nu_thai)', value: 'th_TH_u_ca_buddhist_nu_thai' },
  { title: 'Thailand (th_TH_x_lvariant_TH)', value: 'th_TH_x_lvariant_TH' },
  { title: 'Turkey (tr_TR)', value: 'tr_TR' },
  { title: 'Ukraine (uk_UA)', value: 'uk_UA' },
  { title: 'Vietnam (vi_VN)', value: 'vi_VN' }
].sort((a, b) => a.title.localeCompare(b.title))

export const regions = [
  'af-south-1',
  'ap-east-1',
  'ap-northeast-1',
  'ap-northeast-2',
  'ap-northeast-3',
  'ap-south-1',
  'ap-south-2',
  'ap-southeast-1',
  'ap-southeast-2',
  'ap-southeast-3',
  'ap-southeast-4',
  'ca-central-1',
  'eu-central-1',
  'eu-central-2',
  'eu-north-1',
  'eu-south-1',
  'eu-south-2',
  'eu-west-1',
  'eu-west-2',
  'eu-west-3',
  'il-central-1',
  'me-central-1',
  'me-south-1',
  'sa-east-1',
  'us-east-1',
  'us-east-2',
  'us-west-1',
  'us-west-2'
]

export const defaultRestVariables = [
  { key: '$.restRequest.url' },
  { key: '$.restRequest.method' },
  { key: '$.restRequest.queryParameters' },
  { key: '$.restRequest.requestBody' },
  { key: '$.restRequest.headers' },
  { key: '$.restRequest.pathParameters' },
  { key: '$.restResponse.body' },
  { key: '$.restResponse.statusCode' },
  { key: '$.restResponse.fullBody' }
]

export const MAX_KB_CACHE_TO_DISPLAY = 512

// Languages available for app interface (have translation files in src/lang/)
export const APP_LANGUAGE_DATA = {
  ar: { native: 'العربية', english: 'Arabic' },
  de: { native: 'Deutsch', english: 'German' },
  en: { native: 'English', english: 'English' },
  es: { native: 'Español', english: 'Spanish' },
  fr: { native: 'Français', english: 'French' },
  he: { native: 'עברית', english: 'Hebrew' },
  hi: { native: 'हिन्दी', english: 'Hindi' },
  it: { native: 'Italiano', english: 'Italian' },
  ja: { native: '日本語', english: 'Japanese' },
  nl: { native: 'Nederlands', english: 'Dutch' },
  pl: { native: 'Polski', english: 'Polish' },
  pt: { native: 'Português', english: 'Portuguese' },
  ru: { native: 'Русский', english: 'Russian' },
  sr: { native: 'Srpski', english: 'Serbian' },
  sv: { native: 'Svenska', english: 'Swedish' },
  uk: { native: 'Українська', english: 'Ukrainian' },
  ur: { native: 'اردو', english: 'Urdu' },
  zh: { native: '中文', english: 'Chinese' }
}

// Additional languages for data only (no app interface translations)
export const DATA_ONLY_LANGUAGE_DATA = {
  bg: { native: 'Български', english: 'Bulgarian' },
  bn: { native: 'বাংলা', english: 'Bengali' },
  cs: { native: 'Čeština', english: 'Czech' },
  el: { native: 'Ελληνικά', english: 'Greek' },
  et: { native: 'Eesti', english: 'Estonian' },
  fa: { native: 'فارسی', english: 'Farsi' },
  ga: { native: 'Gaeilge', english: 'Irish' },
  hr: { native: 'Hrvatski', english: 'Croatian' },
  hu: { native: 'Magyar', english: 'Hungarian' },
  id: { native: 'Bahasa Indonesia', english: 'Indonesian' },
  ko: { native: '한국어', english: 'Korean' },
  lt: { native: 'Lietuvių', english: 'Lithuanian' },
  lv: { native: 'Latviešu', english: 'Latvian' },
  mt: { native: 'Malti', english: 'Maltese' },
  ro: { native: 'Română', english: 'Romanian' },
  sk: { native: 'Slovenčina', english: 'Slovak' },
  sl: { native: 'Slovenščina', english: 'Slovenian' },
  tr: { native: 'Türkçe', english: 'Turkish' },
  vi: { native: 'Tiếng Việt', english: 'Vietnamese' }
}

// Combined for data language selectors (templates, translations, etc.)
export const DATA_LANGUAGE_DATA = {
  ...APP_LANGUAGE_DATA,
  ...DATA_ONLY_LANGUAGE_DATA
}

// Derived arrays (sorted alphabetically by English name)
export const APP_LANGUAGES = Object.keys(APP_LANGUAGE_DATA)
  .sort((a, b) => APP_LANGUAGE_DATA[a].english.localeCompare(APP_LANGUAGE_DATA[b].english))
export const DATA_LANGUAGES = Object.keys(DATA_LANGUAGE_DATA)
  .sort((a, b) => DATA_LANGUAGE_DATA[a].english.localeCompare(DATA_LANGUAGE_DATA[b].english))

// Languages the backend OCR step accepts (OcrStepExecutor rejects anything
// else at runtime with "Unknown language"): the tesseract traineddata set
const OCR_EXTRA_LANGUAGE_DATA = {
  da: { native: 'Dansk', english: 'Danish' },
  fi: { native: 'Suomi', english: 'Finnish' }
}

export const OCR_LANGUAGE_DATA = ['bg', 'cs', 'zh', 'hr', 'da', 'nl', 'de', 'el', 'en', 'et', 'fi', 'fr', 'hu', 'it', 'ga', 'lv', 'lt', 'mt', 'pl', 'pt', 'ro', 'ru', 'sk', 'sl', 'es', 'sr', 'sv', 'tr', 'uk']
  .reduce((acc, code) => {
    acc[code] = DATA_LANGUAGE_DATA[code] || OCR_EXTRA_LANGUAGE_DATA[code]

    return acc
  }, {})

export const OCR_LANGUAGES = Object.keys(OCR_LANGUAGE_DATA)
  .sort((a, b) => OCR_LANGUAGE_DATA[a].english.localeCompare(OCR_LANGUAGE_DATA[b].english))

// Backward compatibility aliases
export const LANGUAGE_DATA = APP_LANGUAGE_DATA
export const SUPPORTED_LANGUAGES = APP_LANGUAGES

// All known RTL languages (superset)
const ALL_RTL_LANGUAGES = ['ar', 'he', 'ur', 'fa']

// RTL languages for app interface (must have translations)
export const APP_RTL_LANGUAGES = ALL_RTL_LANGUAGES.filter(lang => APP_LANGUAGES.includes(lang))

// RTL languages for data (all supported, includes Farsi)
export const DATA_RTL_LANGUAGES = ALL_RTL_LANGUAGES.filter(lang => DATA_LANGUAGES.includes(lang))

// Backward compatibility - points to APP because all existing usages are for app interface direction
export const RTL_LANGUAGES = APP_RTL_LANGUAGES

export const DEFAULT_LANGUAGE = 'en'

export const SUPPORTED_RECURRING_TYPES = ['\'SECONDLY\'', '\'MINUTELY\'', '\'HOURLY\'', '\'DAILY\'', '\'WEEKLY\'', '\'MONTHLY\'', '\'YEARLY\'']

// Credential types without a POST /process-credential/test/<type> endpoint (swagger 1.108, Oct 2026):
// the Test button is disabled for them instead of answering "Test unsuccessful" (#5972)
export const CREDENTIAL_TYPES_WITHOUT_TEST = ['GIT', 'MCP_OPENAI_CONNECTOR', 'NEO4J', 'PLUGIN', 'REST', 'REST_BASIC', 'REST_BEARER', 'SCRIPT', 'SECURITY', 'SOAP', 'STRIPE_CONNECT', 'WHATSAPP']

// iCalendar RRULE frequencies as the backend expects them: bare names, no quotes (#6127)
export const ICALENDAR_FREQUENCIES = ['SECONDLY', 'MINUTELY', 'HOURLY', 'DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY']

export const WEEK_DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']

export const TELEMETRY_SEARCH_TYPES = ['range', 'daily', 'monthly', 'yearly']

export const DEFAULT_SIZE_UNIT = 'Kilobytes'

// Snackbar configuration
export const SNACKBAR_TIMEOUT_DEFAULT = 5000

// Flyout menu timing (in milliseconds)
export const FLYOUT_OPEN_DELAY = 100
export const FLYOUT_CLOSE_DELAY = 200
export const FLYOUT_NAVIGATION_CLOSE_DELAY = 150

// UUID regex - primarily for v4 UUIDs (Flowy backend default), but accepts all valid UUID formats.
// Validates structure (8-4-4-4-12 hex with dashes) without enforcing version/variant constraints.
// Admin UI validates format; backend is the authoritative validator for business rules.
export const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

// Backend rejects list requests with size > 512 ("Must be less than or equal to 512"),
// so this is the largest page usable for fetch-all/search calls (DDEV-5713)
export const MAX_PAGE_SIZE = 512

// Backend validates resource comments at 3072 characters on create and update (DDEV-5741)
export const MAX_COMMENT_LENGTH = 3072

// Resource types whose list endpoints accept the `isSystem` query parameter (verified against swagger.json)
export const TYPES_WITH_SYSTEM_FILTER = [
  'PROCESS',
  'PROCESS_CREDENTIAL',
  'TRIGGER_REST',
  'VALIDATION_RULE',
  'TEXT_TEMPLATE'
]

// Resource types whose list endpoints accept the `isAutoGenerated` query parameter (verified against swagger.json)
export const TYPES_WITH_GENERATED_FILTER = [
  'PROCESS',
  'TRIGGER_REST',
  'VALIDATION_RULE'
]

// Resource types whose list endpoints accept the `status` query parameter
export const TYPES_WITH_STATUS_FILTER = [
  'APPLICATION',
  'PROCESS',
  'TRIGGER_REST',
  'TRIGGER_CRON',
  'TRIGGER_MCP',
  'TRIGGER_WHATSAPP',
  'TRIGGER_MESSAGING',
  'TRIGGER_EVENT_HANDLER',
  'TRIGGER_UI',
  'LIBRARY',
  'SCREEN',
  'ENTITY',
  'AI_AGENT',
  'FLOW',
  'PLUGIN',
  'SETTING',
  'PROCESS_CREDENTIAL',
  'STATIC_RESOURCE'
]
